import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpContextToken
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import * as ngCore from '@angular/core';
import { ApiService } from '../services/api.service';
import { Router } from '@angular/router';
import { getCookie } from '../utils/cookie.utils';

let isRefreshing = false;
let refreshFailed = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
const AUTH_RETRY = new HttpContextToken<boolean>(() => false);

function isRefreshEndpoint(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.includes('/auth/refresh');
}

function isPublicAuthPage(router: Router): boolean {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const routerUrl = router.url || '';
  const full = `${pathname} ${hash} ${href} ${routerUrl}`.toLowerCase();
  return full.includes('signup') ||
    full.includes('sign-up') ||
    full.includes('login') ||
    full.includes('network-error');
}

export function authInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const injector = ngCore.inject(ngCore.Injector);
  const router = ngCore.inject(Router);

  // Get apiService lazily to avoid circular dependency with HttpClient
  const apiService = injector.get(ApiService);

  // Skip Zoho requests - let ZohoSupportService handle its own auth
  const url = request.url.toLowerCase();
  const isGeminiRequest = url.includes('generativelanguage.googleapis.com') ||
    url.includes('/gemini-api/');
  const isZohoRequest = url.includes('zoho') ||
    request.headers.has('X-Zoho-Api') ||
    request.headers.get('Authorization')?.startsWith('Zoho-oauthtoken');

  if (isGeminiRequest) {
    return next(request);
  }

  if (isZohoRequest) {
    console.log('[AuthInterceptor] BYPASSING ZOHO REQUEST:', request.url);
    const zohoRequest = request.clone({
      headers: request.headers.delete('X-Zoho-Api')
    });
    return next(zohoRequest);
  }

  // Never attach expired Bearer token to refresh calls — body carries refresh_token
  if (isRefreshEndpoint(url)) {
    const refreshRequest = request.clone({
      headers: request.headers.delete('Authorization').delete('x-session-id')
    });
    return next(refreshRequest);
  }

  // Already refreshed once for this request — don't loop if the new token is still short-lived.
  // HttpContext stays in the app; it is not sent as a header.
  if (request.context.get(AUTH_RETRY)) {
    return next(request);
  }

  // Add auth headers to request if available
  request = addAuthHeaders(request, apiService);

  const token = apiService.getCurrentAuthToken();
  const refreshToken = getCookie('refresh_token');
  const isPublicAsset = url.includes('/assets/') || url.includes('.json') || url.includes('.md');
  const isAuthEndpoint = url.includes('/auth/');
  const isPublicApi = url.includes('/region');

  // Access token missing, or inside dynamic lead time before expiry → refresh once, then send
  const needsRefresh = !!refreshToken && !isAuthEndpoint && !isPublicAsset && !isPublicApi &&
    (!token || apiService.shouldRefreshAccessToken());
  if (needsRefresh) {
    if (isPublicAuthPage(router)) {
      return next(request);
    }
    // If device is offline, skip proactive refresh and proceed with cached/current token if still valid
    if (typeof navigator !== 'undefined' && !navigator.onLine && token && !apiService.isAccessTokenExpired()) {
      return next(request);
    }
    console.log(token
      ? 'Access token near expiry - refreshing before request'
      : 'Access token missing but refresh token available - attempting proactive refresh');
    return handle401Error(request, next, apiService, router).pipe(
      catchError((refreshErr) => {
        // If current access token is not yet expired, allow the original request to proceed with current token
        if (token && !apiService.isAccessTokenExpired()) {
          console.warn('Proactive refresh failed, but current token is still valid. Continuing request.', refreshErr);
          return next(request);
        }
        return throwError(() => refreshErr);
      })
    );
  }

  if (!token && !isAuthEndpoint && !isPublicAsset && !isPublicApi) {
    const isPublic = isPublicAuthPage(router);
    const isOtpFlow = sessionStorage.getItem('otp_verification_in_progress') === 'true';

    if (isPublic || isOtpFlow) {
      return next(request);
    }

    // No access token and no refresh token — end session
    console.log('No access token and no refresh token found - redirecting to login');
    apiService.clearSessionState({ preservePreferences: true });
    router.navigate(['/login']);
    return throwError(() => new HttpErrorResponse({
      status: 401,
      statusText: 'Authentication Required',
      url: request.url
    }));
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const reqUrl = request.url.toLowerCase();
      const isExemptEndpoint =
        reqUrl.includes('/auth/login') ||
        reqUrl.includes('/auth/sign-up') ||
        reqUrl.includes('/auth/verify-otp') ||
        reqUrl.includes('/auth/forgot-password') ||
        reqUrl.includes('/auth/generate-forgot-password') ||
        reqUrl.includes('/auth/send-otp') ||
        reqUrl.includes('/auth/verify');

      if (error.status === 401 && !isRefreshEndpoint(reqUrl) && !isExemptEndpoint && !isPublicAuthPage(router)) {
        return handle401Error(request, next, apiService, router, error);
      }
      return throwError(() => error);
    })
  );
}

function addAuthHeaders(request: HttpRequest<any>, apiService: ApiService): HttpRequest<any> {
  const token = apiService.getCurrentAuthToken();
  const currentUser = apiService.getCurrentUser();
  const sessionId = currentUser?.session_id || currentUser?.sessionId;
  const isSuperAdmin = apiService.isSuperAdmin();
  let userPov = isSuperAdmin ? 'all' : apiService.getUserPov();
  const parkingId = isSuperAdmin ? null : apiService.getCurrentParkingId();
  const orgId = isSuperAdmin ? null : apiService.getCurrentOrganizationId();

  const setHeaders: any = {};
  if (token) {
    setHeaders['Authorization'] = `Bearer ${token}`;
  }
  if (sessionId) {
    setHeaders['x-session-id'] = sessionId;
  }
  if (token) {
    // Protected requests always require x-user-pov
    if (!userPov) {
      userPov = parkingId ? 'parking' : (orgId ? 'organization' : 'all');
    }

    if (userPov === 'parking') {
      if (parkingId) {
        setHeaders['x-user-pov'] = 'parking';
        setHeaders['x-parking-id'] = parkingId;
      } else if (orgId) {
        // Fallback to organization if parking ID is missing
        setHeaders['x-user-pov'] = 'organization';
      } else {
        setHeaders['x-user-pov'] = 'all';
      }
    } else {
      setHeaders['x-user-pov'] = userPov;
    }
  } else if (userPov) {
    // Unauthenticated/public request - only add if valid
    if (userPov === 'parking' && parkingId) {
      setHeaders['x-user-pov'] = 'parking';
      setHeaders['x-parking-id'] = parkingId;
    } else if (userPov !== 'parking') {
      setHeaders['x-user-pov'] = userPov;
    }
  }
  if (orgId) {
    setHeaders['x-org-id'] = orgId;
  }

  // Add x-role context for backend authorization
  const currentRole =
    sessionStorage.getItem('current_role') ||
    localStorage.getItem('current_role') ||
    localStorage.getItem('user_role') ||
    currentUser?.type ||
    currentUser?.current_role ||
    currentUser?.role?.name;
  if (currentRole) {
    setHeaders['x-role'] = String(currentRole).toLowerCase();
  }

  return request.clone({ setHeaders });
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  apiService: ApiService,
  router: Router,
  originalError?: any
): Observable<HttpEvent<any>> {
  if (isPublicAuthPage(router)) {
    return throwError(() => originalError || new Error('Public page - skipping auth redirect'));
  }

  const refreshToken = getCookie('refresh_token');

  if (!refreshToken) {
    console.log('Token expired and no refresh token - logging out');
    apiService.clearSessionState({ preservePreferences: true });
    router.navigate(['/login']);
    return throwError(() => new Error('Session expired'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshFailed = false;
    refreshTokenSubject.next(null);

    console.log('Attempting to refresh token using /auth/refresh-token API');
    return apiService.refreshToken().pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshFailed = false;
        const accessToken = apiService.getCurrentAuthToken();

        if (accessToken && !apiService.isAccessTokenExpired()) {
          console.log('Token refresh successful via /auth/refresh-token API');
          refreshTokenSubject.next(accessToken);
          return next(retryAuthed(request, apiService));
        }
        throw new Error('No access token in refresh response');
      }),
      catchError((err) => {
        isRefreshing = false;
        const recoveredToken = apiService.getCurrentAuthToken();
        // A parallel refresh may already have stored a new access token.
        // Do not wipe that session just because this subscriber saw an error.
        if (recoveredToken && !apiService.isAccessTokenExpired()) {
          refreshFailed = false;
          refreshTokenSubject.next(recoveredToken);
          return next(retryAuthed(request, apiService));
        }

        refreshFailed = true;
        refreshTokenSubject.next(null);
        // Keep the browser session on network/server blips.
        // End it only when the server rejects the refresh token (logout or admin revoke).
        if (isSessionRevoked(err) && !isPublicAuthPage(router)) {
          console.error('Refresh token rejected, logging out:', err);
          apiService.clearSessionState({ preservePreferences: true });
          router.navigate(['/login']);
        } else {
          console.warn('Refresh failed temporarily, keeping session:', err);
        }
        return throwError(() => err);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null || refreshFailed),
    take(1),
    switchMap((token) => {
      const latest = apiService.getCurrentAuthToken();
      if (!token || refreshFailed || !latest || apiService.isAccessTokenExpired()) {
        return throwError(() => new Error('Session expired'));
      }
      return next(retryAuthed(request, apiService));
    })
  );
}

function isSessionRevoked(err: any): boolean {
  const status = Number(err?.status ?? err?.error?.statusCode ?? 0);
  return status === 401 || status === 403;
}

function retryAuthed(request: HttpRequest<any>, apiService: ApiService): HttpRequest<any> {
  const context = request.context;
  context.set(AUTH_RETRY, true);
  return addAuthHeaders(request, apiService).clone({ context });
}
